import React from 'react';
import type { AppNotification } from '../../types/notifications';
import { formatNotificationTime } from '../../utils/formatNotificationTime';
import { plainNotificationPreview, splitNotificationPreviewMentions } from '../../utils/notificationPreview';

type Props = {
  notification: AppNotification;
  showUnreadDot?: boolean;
  /** Z. B. um das Datum beim Darüberfahren für eine Aktion auszublenden. */
  timeClassName?: string;
};

function isPayrollChangeRequestNotification(notification: AppNotification): boolean {
  return (
    notification.type === 'payroll_change_request' ||
    notification.source_type === 'time_entry_change_request'
  );
}

function isWorkReportSubmittedNotification(notification: AppNotification): boolean {
  return (
    notification.type === 'work_report_submitted' ||
    notification.source_type === 'lead_document'
  );
}

function isScheduledJobNotification(notification: AppNotification): boolean {
  return notification.type === 'ai_scheduled_job' || notification.source_type === 'ai_scheduled_job';
}

function isInboundLeadNotification(notification: AppNotification): boolean {
  return notification.type === 'inbound_lead' || notification.source_type === 'lead_ingest';
}

function isTaskAssignedNotification(notification: AppNotification): boolean {
  return notification.type === 'task_assigned' || notification.source_type === 'task';
}

function leadSummaryLine(contextLabel: string, customerName?: string): string | null {
  const lead = contextLabel.trim();
  const customer = String(customerName ?? '').trim();
  if (!lead && !customer) return null;
  if (!customer || customer === lead) return lead || customer;
  if (!lead) return customer;
  return `${lead} · ${customer}`;
}

function PreviewText({ text }: { text: string }) {
  const parts = React.useMemo(() => splitNotificationPreviewMentions(text), [text]);
  if (!parts.length) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        part.type === 'mention' ? (
          <span key={i} className="font-semibold text-emerald-700">
            {part.value}
          </span>
        ) : (
          <React.Fragment key={i}>{part.value}</React.Fragment>
        ),
      )}
    </>
  );
}

export function NotificationItemContent({ notification: n, showUnreadDot = false, timeClassName }: Props) {
  const actor = n.actor_display_name?.trim() || 'Jemand';
  const taskAssigned = isTaskAssignedNotification(n);
  // To-do: Titel in der Vorschau, darunter Code · Fälligkeit · Anfrage (body_preview).
  const preview = taskAssigned ? n.context_label?.trim() ?? '' : plainNotificationPreview(n.body_preview ?? '');
  const payroll = isPayrollChangeRequestNotification(n);
  const workReport = isWorkReportSubmittedNotification(n);
  const inboundLead = isInboundLeadNotification(n);
  const scheduledJob = isScheduledJobNotification(n);
  const leadLine = taskAssigned
    ? n.body_preview?.trim() || null
    : payroll
    ? (n.context_label?.trim() ? `Mitarbeiter: ${n.context_label.trim()}` : null)
    : workReport || inboundLead
      ? (n.context_label?.trim() ? `Kunde: ${n.context_label.trim()}` : null)
      : leadSummaryLine(n.context_label ?? '', n.context_customer_name);

  return (
    <div className="flex gap-2 min-w-0">
      {showUnreadDot ? (
        !n.is_read ? (
          <span className="mt-2 w-2 h-2 shrink-0 rounded-full bg-pipedrive-blue" aria-hidden />
        ) : (
          <span className="w-2 shrink-0" aria-hidden />
        )
      ) : null}

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm leading-snug text-app-text">
            <span className="font-semibold text-app-text">{actor}</span>
            <span className="text-app-text-muted">
              {taskAssigned
                ? ' hat dir ein To-do zugewiesen'
                : payroll
                ? ' hat einen Zeiterfassungs-Antrag eingereicht'
                : workReport
                  ? ' hat einen Arbeitsbericht eingereicht'
                  : inboundLead
                    ? ' hat ein neues Projekt eingeliefert'
                    : scheduledJob
                      ? ' hat einen geplanten Lauf beendet'
                      : ' hat dich erwähnt'}
            </span>
          </p>
          <time className={`shrink-0 text-[11px] leading-snug text-slate-400 whitespace-nowrap ${timeClassName ?? ''}`}>
            {formatNotificationTime(n.created_at)}
          </time>
        </div>

        {preview ? (
          <div className="mt-2 flex gap-2.5 rounded-r-md bg-app-surface-muted/90 py-2 pl-0 pr-2">
            <div className={`w-1 shrink-0 rounded-full ${taskAssigned ? 'bg-violet-500' : payroll ? 'bg-amber-500' : workReport ? 'bg-sky-500' : inboundLead ? 'bg-indigo-500' : scheduledJob ? 'bg-pipedrive-blue' : 'bg-emerald-500'}`} aria-hidden />
            <p className="min-w-0 text-sm leading-relaxed text-app-text-muted line-clamp-3">
              {taskAssigned || payroll || workReport || inboundLead || scheduledJob ? preview : <PreviewText text={preview} />}
            </p>
          </div>
        ) : null}

        {leadLine ? (
          <p className="mt-2 truncate text-xs font-medium text-app-text-subtle">{leadLine}</p>
        ) : null}
      </div>
    </div>
  );
}
